import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n, useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Globe, User as UserIcon, ShieldCheck, Trash2 } from "lucide-react";

export default function Settings() {
  const t = useT();
  const { lang, setLang } = useI18n();
  const { user, refresh } = useAuth();
  const qc = useQueryClient();

  const [name, setName] = useState(user?.name || user?.display_name || "");
  const [savingName, setSavingName] = useState(false);

  const consent = useQuery({
    queryKey: ["consent"],
    queryFn: async () => (await api.get("/consent")).data,
    retry: false,
  });

  const saveLang = async (newLang) => {
    setLang(newLang);
    try {
      await api.patch("/profile/language", { language: newLang });
      toast.success(t("common.saved"));
    } catch (e) { /* silent — local set already applied */ }
  };

  const saveName = async () => {
    setSavingName(true);
    try {
      await api.patch("/me/account", { name });
      toast.success(t("common.saved"));
      await refresh();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingName(false); }
  };

  const toggleConsent = async (key, value) => {
    try {
      await api.post("/consent", { [key]: value });
      qc.invalidateQueries({ queryKey: ["consent"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const consentItems = consent.data ? Object.entries(consent.data).filter(([_, v]) => typeof v === "boolean") : [];

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("common.settings")} title={t("settings.profile")} />

      <section className="rounded-3xl bg-white shadow-card p-6 md:p-8" data-testid="settings-profile">
        <div className="flex items-center gap-2 mb-4">
          <UserIcon className="h-4 w-4 text-sage-600" strokeWidth={1.75} />
          <h2 className="font-heading text-lg text-ink-900">{t("settings.profile")}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>{t("common.name")}</Label>
            <Input data-testid="settings-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl bg-cream-50 h-11 mt-1" />
          </div>
          <div>
            <Label>{t("common.email")}</Label>
            <Input value={user?.email || ""} disabled className="rounded-xl bg-cream-50 h-11 mt-1" />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button data-testid="settings-save-name" onClick={saveName} disabled={savingName} className="rounded-full bg-sage-500 hover:bg-sage-600 h-11 px-5">
            {savingName ? t("common.saving") : t("common.save")}
          </Button>
        </div>
      </section>

      <section className="rounded-3xl bg-white shadow-card p-6 md:p-8" data-testid="settings-language">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="h-4 w-4 text-sage-600" strokeWidth={1.75} />
          <h2 className="font-heading text-lg text-ink-900">{t("settings.languagePref")}</h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => saveLang("en")}
            data-testid="settings-lang-en"
            className={`rounded-full px-4 py-2 text-sm border ${lang === "en" ? "bg-sage-500 text-white border-sage-500" : "bg-white border-border text-ink-700 hover:text-ink-900"}`}
          >
            {t("common.english")}
          </button>
          <button
            onClick={() => saveLang("nl")}
            data-testid="settings-lang-nl"
            className={`rounded-full px-4 py-2 text-sm border ${lang === "nl" ? "bg-sage-500 text-white border-sage-500" : "bg-white border-border text-ink-700 hover:text-ink-900"}`}
          >
            {t("common.dutch")}
          </button>
        </div>
      </section>

      <section className="rounded-3xl bg-white shadow-card p-6 md:p-8" data-testid="settings-consent">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="h-4 w-4 text-sage-600" strokeWidth={1.75} />
          <h2 className="font-heading text-lg text-ink-900">{t("client.yourConsent")}</h2>
        </div>
        {consentItems.length === 0 && (
          <div className="text-sm text-ink-500">{t("common.empty")}</div>
        )}
        <div className="space-y-2">
          {consentItems.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between rounded-2xl border border-border p-3">
              <div className="text-sm text-ink-900 capitalize">{k.replaceAll("_", " ")}</div>
              <Switch checked={!!v} onCheckedChange={(nv) => toggleConsent(k, nv)} data-testid={`consent-${k}`} />
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-3xl bg-white shadow-card p-6 md:p-8 border-l-4 border-terracotta-500" data-testid="settings-danger">
        <div className="flex items-center gap-2 mb-3">
          <Trash2 className="h-4 w-4 text-terracotta-500" strokeWidth={1.75} />
          <h2 className="font-heading text-lg text-ink-900">{t("settings.delete")}</h2>
        </div>
        <p className="text-sm text-ink-700 mb-4">{t("public.delete.subtitle")}</p>
        <a
          href="/delete-account"
          data-testid="settings-delete-account-link"
          className="inline-flex items-center gap-2 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white h-11 px-5 text-sm"
        >
          <Trash2 className="h-4 w-4" strokeWidth={1.75} />
          {t("settings.delete")}
        </a>
      </section>
    </div>
  );
}
